import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useMemo, useState } from "react";

type TagItem = BadgeRedoListItem<string>;

function toItem(tag: string): TagItem {
  return {
    id: tag,
    type: "badge",
    value: tag,
    badge: { text: tag, color: "gray" },
  };
}

/**
 * Multi-select over product-family tags. Unlike the other pickers this queries
 * per keystroke: tags are an OpenSearch aggregation, not a bounded list.
 */
export const SuppliesFamilyTagSelect = memo(function SuppliesFamilyTagSelect({
  tags,
  setTags,
  disabled,
}: {
  tags: string[];
  setTags: (tags: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMerchantRpcClientContext);
  const [searchString, setSearchString] = useState("");

  const tagsLoad = useLoad(
    async (signal) => {
      const result = await client.getSuppliesProductFamilyTags(
        { search: searchString || undefined },
        { signal },
      );
      return result.tags;
    },
    [client, searchString],
  );

  const options = useMemo(
    () => (tagsLoad.value ?? []).map(toItem),
    [tagsLoad.value],
  );
  const selectedOptions = useMemo(() => tags.map(toItem), [tags]);

  return (
    <RedoTagsDropdownInput
      description={t(
        "Matches when any cart item's product family has one of these tags.",
      )}
      disabled={disabled}
      label={t("Family tags")}
      loading={tagsLoad.pending}
      options={options}
      placeholder={t("Select tags")}
      searchPlaceholder={t("Search tags...")}
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={(items) => setTags(items.map((item) => item.value))}
      size="xs"
    />
  );
});
